/**
 * The single source of truth for "this tab is an impersonation tab".
 *
 * An admin jumps into a seller account in a second tab (see
 * docs/plans/2026-09-16-001-feat-admin-impersonation-plan.md, U6). That
 * tab must never touch the admin's session, which lives in `localStorage`
 * and is shared by every tab of the origin. So the impersonation tab keeps
 * everything in `sessionStorage` (per tab, gone with the tab):
 *
 * | key                                   | content                        |
 * | ------------------------------------- | ------------------------------ |
 * | `designdeck.impersonation`            | the marker (target, expiry,    |
 * |                                       | why the session ended)         |
 * | `designdeck.impersonation.access-token` | the seller access token      |
 * | the persisted store names             | tab-local copies of the stores |
 *
 * The marker is never removed by the app. Once a tab carries it — even
 * after the session ended — the tab stays an impersonation tab for its
 * lifetime, so it can never fall back to the admin's `localStorage` session.
 *
 * Nothing here imports the API client or a store: the client and the
 * stores import from here.
 */

import { createJSONStorage, type StateStorage } from 'zustand/middleware';
import { isPreviewFrame, previewStorage } from './preview-frame';

export const IMPERSONATION_MARKER_KEY = 'designdeck.impersonation';
export const IMPERSONATION_ACCESS_TOKEN_KEY =
  'designdeck.impersonation.access-token';

/**
 * Names of every Zustand store persisted through `persistStorage()`. Their
 * tab-local copies are wiped when a session begins and when it ends. A test
 * checks each store's persist name against this list.
 */
export const PERSISTED_STORE_KEYS = [
  'auth-storage',
  'product-selection-storage',
  'canvas-state-manager',
  'direct-sale-storage',
] as const;

export type ImpersonationEndReason =
  | 'expired'
  | 'ended'
  | 'revoked'
  | 'code_invalid';

export interface ImpersonationMarker {
  targetUserId: string;
  targetEmail: string;
  targetName: string;
  /** ISO timestamp at which the access token stops working. */
  expiresAt: string;
  endedReason: ImpersonationEndReason | null;
}

const END_REASONS: readonly ImpersonationEndReason[] = [
  'expired',
  'ended',
  'revoked',
  'code_invalid',
];

/** Where an ended session reloads to; App renders the end screen anywhere. */
const ENDED_RELOAD_PATH = '/';

/**
 * `sessionStorage`, or null where the browser blocks it (an iframe with
 * third-party storage disabled throws on mere property access).
 */
function tabStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseMarker(raw: string | null): ImpersonationMarker | null {
  if (raw === null) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(value)) return null;

  const { targetUserId, targetEmail, targetName, expiresAt, endedReason } =
    value;
  if (
    typeof targetUserId !== 'string' ||
    targetUserId === '' ||
    typeof targetEmail !== 'string' ||
    typeof targetName !== 'string' ||
    typeof expiresAt !== 'string' ||
    Number.isNaN(Date.parse(expiresAt))
  ) {
    return null;
  }
  if (
    endedReason !== null &&
    !END_REASONS.includes(endedReason as ImpersonationEndReason)
  ) {
    return null;
  }

  return {
    targetUserId,
    targetEmail,
    targetName,
    expiresAt,
    endedReason: endedReason as ImpersonationEndReason | null,
  };
}

/** The marker of this tab, or null (no marker, malformed, storage blocked). */
export function readImpersonation(): ImpersonationMarker | null {
  const storage = tabStorage();
  if (!storage) return null;
  try {
    return parseMarker(storage.getItem(IMPERSONATION_MARKER_KEY));
  } catch {
    return null;
  }
}

/** True for a tab that holds (or held) an impersonation session. */
export function isImpersonationTab(): boolean {
  return readImpersonation() !== null;
}

/**
 * Why this tab shows the end screen, or null while the session runs (or
 * the tab is no impersonation tab). Pure: besides a recorded `endedReason`
 * it also treats a passed expiry or a missing token as expired, so a tab
 * reopened after the fact never renders the app without a session.
 */
export function impersonationEndReason(
  now: number = Date.now()
): ImpersonationEndReason | null {
  const marker = readImpersonation();
  if (!marker) return null;
  if (marker.endedReason) return marker.endedReason;
  if (Date.parse(marker.expiresAt) <= now) return 'expired';
  if (!tabStorage()?.getItem(IMPERSONATION_ACCESS_TOKEN_KEY)) return 'expired';
  return null;
}

function clearTabSession(storage: Storage): void {
  storage.removeItem(IMPERSONATION_ACCESS_TOKEN_KEY);
  for (const key of PERSISTED_STORE_KEYS) {
    storage.removeItem(key);
  }
}

export interface BeginImpersonationInput {
  accessToken: string;
  expiresAt: string;
  user: { id: string; email: string; name?: string | null };
}

/**
 * Turns this tab into an impersonation tab. The caller reloads afterwards
 * (`location.replace`), so every store hydrates from the tab storage.
 * Never touches `localStorage`.
 */
export function beginImpersonationSession({
  accessToken,
  expiresAt,
  user,
}: BeginImpersonationInput): void {
  const storage = tabStorage();
  if (!storage) {
    throw new Error('Tab storage is unavailable');
  }
  // Leftovers of an earlier session in this tab must not hydrate into the
  // new account.
  clearTabSession(storage);

  const marker: ImpersonationMarker = {
    targetUserId: user.id,
    targetEmail: user.email,
    targetName: user.name ?? '',
    expiresAt,
    endedReason: null,
  };
  storage.setItem(IMPERSONATION_MARKER_KEY, JSON.stringify(marker));
  storage.setItem(IMPERSONATION_ACCESS_TOKEN_KEY, accessToken);
}

/**
 * How long a still-living page waits after `location.replace` before it
 * treats the navigation as cancelled (the admin chose "stay" on the
 * unsaved-changes prompt) and accepts a new end request. Browsers suspend
 * the page while that prompt is open, so the timer only runs once the
 * decision is made.
 */
export const END_NAVIGATION_CANCEL_MS = 1500;

interface PendingEnd {
  /** The first reason requested; written to the marker on `pagehide`. */
  reason: ImpersonationEndReason;
  /** True once any request came from a dead token (expired, revoked). */
  dead: boolean;
}

let pendingEnd: PendingEnd | null = null;
let navigationPending = false;
let cancelTimer: ReturnType<typeof setTimeout> | null = null;

function isDeadReason(reason: ImpersonationEndReason): boolean {
  return reason === 'expired' || reason === 'revoked';
}

/**
 * Runs when the page really goes away: only then are the token and the
 * store copies removed and the reason recorded. `pagehide` fires before
 * `unload`, and `sessionStorage` writes made there persist for the same
 * tab, so the reloaded document finds the marker with its reason.
 */
function handlePageHide(): void {
  const end = pendingEnd;
  pendingEnd = null;
  navigationPending = false;
  if (cancelTimer !== null) {
    clearTimeout(cancelTimer);
    cancelTimer = null;
  }
  if (!end) return;

  const storage = tabStorage();
  if (!storage) return;
  const marker = readImpersonation();
  clearTabSession(storage);
  if (marker && !marker.endedReason) {
    storage.setItem(
      IMPERSONATION_MARKER_KEY,
      JSON.stringify({ ...marker, endedReason: end.reason })
    );
  }
}

/**
 * True while an end of a dead session (expired or revoked token) is on its
 * way. Unsaved work can no longer be saved then, so the unsaved-changes
 * guard must not hold the tab on a prompt.
 */
export function isEndingDeadImpersonation(): boolean {
  return pendingEnd?.dead === true;
}

/**
 * Ends the session of this tab: reloads, and once the page actually unloads
 * (`pagehide`) removes the token and the tab copies of the stores and
 * records the reason on the marker (which stays), so the app comes up on
 * the end screen with no store state left in memory. The first recorded
 * reason wins. Never touches `localStorage`.
 *
 * Nothing is cleared before the unload: if the admin cancels the
 * unsaved-changes prompt, the tab keeps a working session and can still
 * save. While a navigation is pending, further calls do not navigate again
 * (a 401 from a save would otherwise re-raise the prompt in a loop); after
 * END_NAVIGATION_CANCEL_MS on a still-living page a new call may. The
 * `pagehide` listener stays registered after a cancel, so a later unload of
 * the tab still ends the session (fail closed).
 */
export function endImpersonationSession(reason: ImpersonationEndReason): void {
  if (!pendingEnd) {
    pendingEnd = { reason, dead: isDeadReason(reason) };
    window.addEventListener('pagehide', handlePageHide, { once: true });
  } else if (isDeadReason(reason)) {
    pendingEnd.dead = true;
  }

  if (navigationPending) return;
  navigationPending = true;
  window.location.replace(ENDED_RELOAD_PATH);
  // Only fires if the page is still alive, i.e. the navigation was
  // cancelled (or is slow — then a repeated replace merely restarts it).
  cancelTimer = setTimeout(() => {
    cancelTimer = null;
    navigationPending = false;
  }, END_NAVIGATION_CANCEL_MS);
}

// ═══════════════════════════════════════════════════════════════════
// PERSIST STORAGE
// ═══════════════════════════════════════════════════════════════════

/**
 * The storage that backs persisted state in this document: an area of its
 * own for the seller's Direct Sale preview frame (lib/preview-frame.ts),
 * sessionStorage in an impersonation tab, localStorage otherwise.
 */
function activeStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
  if (isPreviewFrame()) return previewStorage;
  return isImpersonationTab() ? window.sessionStorage : window.localStorage;
}

/**
 * Chooses the storage on every call rather than once.
 *
 * zustand v5's `createJSONStorage(getStorage)` calls `getStorage()` exactly
 * once, when `persist` is created — that is at module import. A plain
 * `createJSONStorage(() => activeStorage())` would therefore freeze the
 * choice at import time. In the browser that would still be correct (the
 * exchange page writes the marker and then does a full reload, so the
 * marker exists before any store module is evaluated), but it would tie
 * correctness to import order and make the choice untestable. Delegating
 * per call keeps the choice in one place, costs one `sessionStorage` read
 * per persist operation, and a store hydrating after the reload reads the
 * marker at that moment.
 */
const tabAwareStorage: StateStorage = {
  getItem: (name) => activeStorage().getItem(name),
  setItem: (name, value) => activeStorage().setItem(name, value),
  removeItem: (name) => activeStorage().removeItem(name),
};

/**
 * JSON storage for Zustand `persist`: `sessionStorage` in an impersonation
 * tab, `localStorage` otherwise. The initial `activeStorage()` call keeps
 * the old failure mode: where the storage is blocked, `createJSONStorage`
 * returns undefined and `persist` runs without storage, as before.
 */
export function persistStorage<S>() {
  return createJSONStorage<S>(() => {
    activeStorage();
    return tabAwareStorage;
  });
}
